import React, { useContext, useEffect, useMemo } from "react";
import { Gamepad2, Star, Trophy, Zap, ShieldCheck, MonitorSmartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { DataContext } from "../../Context/DataContext";

const FeaturedSection = () => {
  const gameData = useContext(DataContext);

  // Generate a daily random set of games
  const dailyGames = useMemo(() => {
    if (!gameData || gameData.length === 0) return [];
    
    // Get the current day as an integer, with an offset so the featured set differs from the gallery
    const daySeed = Math.floor(Date.now() / (1000 * 60 * 60 * 24)) + 999;
    
    // Seeded pseudo-random number generator
    const random = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    const copy = [...gameData];
    // Fisher-Yates shuffle seeded with the current day
    let currentSeed = daySeed;
    for (let i = copy.length - 1; i > 0; i--) {
      currentSeed++;
      const j = Math.floor(random(currentSeed) * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    
    // Set proper number of items to display (e.g., 6 items for 2 full rows)
    return copy.slice(0, 6);
  }, [gameData]);

useEffect(() => {
  const loadAOS = async () => {
    const AOS = await import('aos');
    AOS.init({
      duration: 500,
      easing: "ease-out-cubic",
      once: true,
      offset: 100
    });
  };
  loadAOS();
}, []);

const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
};

  return (
    <section className="bg-gradient-to-b lg:px-8 from-[#0a0f1f] to-[#141a2e] sm:py-20 py-12 ">
      <div className="container mx-auto px-4">
        
        {/* 🚀 NATURAL HUMAN WRITTEN INTRO SECTION FOR SEO */}
        <div className="text-center mb-16" data-aos="fade-up" data-aos-delay="100">
          <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-white mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
              🔥 Most Played & Trending
            </span>{" "}
            Games
          </h2>
          <div className="flex justify-center">
            <p className="text-gray-400 sm:text-lg text-[16px] md:w-4/5 lg:w-3/4 leading-relaxed font-light">
              Not sure what to play today? Take a look at our current list of the <strong>most popular free online games</strong>. 
              These are the games that our community is playing the most right now. 
              From fast-paced <strong>action games</strong> to brain-teasing <strong>puzzles</strong>, these top-rated titles are easy to get into but hard to put down. 
              You don't need to install anything—just click on your favorite game and start playing instantly on your phone or computer.
            </p>
          </div>
        </div>

        {/* GAME CARDS */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
          {dailyGames?.slice(0,3).map((game, index) => {

            const gameSlug = createSlug(game?.name);

            return(
              <div
              key={index}
              data-aos="zoom-in"
              data-aos-delay={`${300 + index * 100}`}
              className="relative bg-[#1b2236] rounded-3xl cursor-pointer overflow-hidden transition-all duration-500 group shadow-[0_0_20px_rgba(0,0,0,0.3)] hover:shadow-[0_0_35px_rgba(0,150,255,0.5)] hover:scale-105 hover:-translate-y-2 border border-white/5 hover:border-cyan-500/30"
            >
              <Link to={`/play/${gameSlug}`} state={{ game: game }} aria-label={`Play ${game?.name} free online`}>
                <div className="relative overflow-hidden rounded-t-3xl">
                  <picture>
                    <source srcSet={game?.image} type="image/webp" />
                    <img
                      src={game?.image}
                      alt={`Play ${game?.name} instantly on Game Zone Hub`}
                      title={`Play ${game?.name}`}
                      className="w-full h-56 object-cover transform transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1b2236] via-transparent to-white/10 opacity-80 group-hover:opacity-100 transition-opacity duration-700"></div>
                  <div className="absolute top-4 right-4 bg-yellow-500/90 backdrop-blur-md text-black text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                        <Star size={14} fill="black" /> Trending
                    </div>
                </div>
              </Link>

              <div className="p-6 relative z-10 flex flex-col h-[200px]">
                <h3
                  className="sm:text-xl text-lg font-bold text-white line-clamp-1 group-hover:text-cyan-300 transition-colors"
                  data-aos="fade-up"
                  data-aos-delay={`${400 + index * 150}`}
                >
                  {game?.name}
                </h3>
                
                <p
                    className="text-gray-400 text-sm mt-3 line-clamp-2 min-h-[40px] flex-grow leading-relaxed"
                    data-aos="fade-up"
                    data-aos-delay={`${450 + index * 150}`}
                  >
                    {game?.info || game?.desc || `Play ${game?.name} for free. Join the fun and beat high scores in this exciting browser game.`}
                </p>

                <Link 
                  to={`/play/${gameSlug}`} 
                  state={{ game: game }}
                  data-aos="flip-left"
                  data-aos-delay={`${500 + index * 150}`}
                  className="mt-5 px-6 py-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.6)] w-full"
                  title={`Play ${game?.name} Now`}
                >
                  <Gamepad2 size={18} /> Play Now 
                </Link>
              </div>

              <div className="absolute -z-10 bottom-0 right-0 w-40 h-40 bg-cyan-600/20 rounded-full mix-blend-overlay blur-3xl group-hover:scale-150 transition-transform duration-1000 pointer-events-none"></div>
            </div>
            )
          })}
        </div>
        
        {/* 🚀 MASSIVELY EXPANDED NATURAL TEXT EDITOR'S CHOICE SECTION */}
        <div className="sm:mt-20 mt-10 max-w-5xl mx-auto border-t border-white/10 pt-12" data-aos="fade-up">
            <div className="flex flex-col items-center text-center mb-8">
                <div className="flex items-center justify-center gap-2 mb-4 text-yellow-400 bg-yellow-500/10 px-6 py-2 rounded-full border border-yellow-500/20">
                    <Trophy size={22} /> <span className="text-sm font-extrabold tracking-widest uppercase">Editor's Choice</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 mt-3">Why These Games Stand Out</h3>
                <p className="text-gray-400 text-base leading-relaxed max-w-3xl">
                    Out of all the games on our site, these are the ones we keep coming back to. Our team plays a lot of games, and we only feature the ones that are genuinely fun, have great controls, and look awesome.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <Zap className="text-blue-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Smooth Performance</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">No one likes a laggy game. These top picks are built with modern web tech to make sure they run smoothly. You get great graphics and fast action without needing an expensive gaming PC.</p>
                </div>
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <MonitorSmartphone className="text-purple-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Play on Any Device</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">Whether you are clicking a mouse on your desktop or tapping the screen on your mobile phone, these games respond perfectly. They resize automatically so the fun never stops, no matter what device you use.</p>
                </div>
                <div className="bg-black/30 p-6 rounded-2xl border border-white/5">
                    <ShieldCheck className="text-green-400 w-8 h-8 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Safe and Secure</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">Because you play these games directly in your browser, you never have to worry about accidentally downloading something harmful. It is a clean, safe way to enjoy your free time.</p>
                </div>
            </div>
        </div>

      </div>
    </section>
  );
};

export default FeaturedSection;